import { useEffect, useRef } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const ROOT_DASHBOARD_PATHS = new Set([
  '/consumer/home',
  '/provider/dashboard',
  '/admin/dashboard',
]);

const DOUBLE_BACK_WINDOW_MS = 1800; // Android release build trigger

export default function AndroidBackHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const lastBackAtRef = useRef(0);

  useEffect(() => {
    lastBackAtRef.current = 0;

    if (Capacitor.getPlatform() !== 'android') {
      return undefined;
    }

    let mounted = true;
    let listenerHandle = null;

    const handleBackButton = async ({ canGoBack }) => {
      if (!mounted) return;

      if (document.body.dataset.rangerTutorialOpen === '1') {
        window.dispatchEvent(new Event('resqly:ranger-tutorial-back'));
        return;
      }

      if (ROOT_DASHBOARD_PATHS.has(location.pathname)) {
        const now = Date.now();

        if (now - lastBackAtRef.current <= DOUBLE_BACK_WINDOW_MS) {
          lastBackAtRef.current = 0;
          toast.dismiss('resqly-exit-confirmation');
          await CapacitorApp.exitApp();
          return;
        }

        lastBackAtRef.current = now;
        toast.info('Press back again to exit RESQLY', {
          id: 'resqly-exit-confirmation',
          duration: DOUBLE_BACK_WINDOW_MS,
        });

        window.setTimeout(() => {
          if (Date.now() - lastBackAtRef.current > DOUBLE_BACK_WINDOW_MS) {
            lastBackAtRef.current = 0;
          }
        }, DOUBLE_BACK_WINDOW_MS + 50);
        return;
      }

      if (canGoBack) {
        navigate(-1);
        return;
      }

      await CapacitorApp.exitApp();
    };

    CapacitorApp.addListener('backButton', handleBackButton).then((handle) => {
      if (mounted) {
        listenerHandle = handle;
      } else {
        handle.remove();
      }
    });

    return () => {
      mounted = false;
      listenerHandle?.remove();
    };
  }, [location.pathname, navigate]);

  return null;
}
